/**
 * Inline SVG icons used by the editor toolbar for glyphs that are not part of
 * the SHUI icon font (italic, underline, strikethrough).
 *
 * `<sh-icon>` accepts a custom path in the form `url('...')` for its `icon`
 * input and paints it via shadow-DOM `background-image`. Because of that the
 * SVG's own paint determines the visible color (CSS `currentColor` does not
 * resolve through `background-image`, and the inner glyph element is not
 * exposed as a CSS `::part`, so we cannot recolor just the SVG from outside
 * with a `filter`).
 *
 * To still match the active theme we ship a black variant for light mode and
 * a white variant for dark mode and let the editor component pick the right
 * set. Embedding the SVGs as data-URIs keeps them inside `frontend-lib` so no
 * per-app `assets/icons/*.svg` files are required.
 */

const ITALIC_PATH = 'M10 4v3h2.21l-3.42 8H6v3h8v-3h-2.21l3.42-8H18V4z';
const UNDERLINE_PATH =
    'M12 17c3.31 0 6-2.69 6-6V3h-2.5v8c0 1.93-1.57 3.5-3.5 3.5S8.5 12.93 8.5 11V3H6v8c0 3.31 2.69 6 6 6zm-7 2v2h14v-2z';

const buildItalicSvg = (color: string): string =>
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="${color}" d="${ITALIC_PATH}"/></svg>`;

const buildUnderlineSvg = (color: string): string =>
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="${color}" d="${UNDERLINE_PATH}"/></svg>`;

// Strikethrough glyph: a literal "S" with a horizontal bar drawn through it.
// Using an SVG <text> element keeps the letterform crisp and matches the
// "S with a line through" mental model users expect from the toolbar button.
const buildStrikethroughSvg = (color: string): string =>
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><text x="12" y="18" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="20" font-weight="700" fill="${color}">S</text><rect x="3" y="11" width="18" height="2" fill="${color}"/></svg>`;

const toIconUrl = (svg: string): string => `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`;

const buildIconSet = (color: string) => ({
    italic: toIconUrl(buildItalicSvg(color)),
    underline: toIconUrl(buildUnderlineSvg(color)),
    strike: toIconUrl(buildStrikethroughSvg(color))
});

export type ShEditorIconSet = ReturnType<typeof buildIconSet>;

export const SH_EDITOR_ICONS_LIGHT: ShEditorIconSet = buildIconSet('black');
export const SH_EDITOR_ICONS_DARK: ShEditorIconSet = buildIconSet('white');

/** @deprecated Kept for backward compatibility. Prefer the theme-specific exports. */
export const SH_EDITOR_ICONS = SH_EDITOR_ICONS_LIGHT;
